import { cn } from '@/lib/utils';
import { primaryGradientActive } from '@/lib/gradientStyles';

/**
 * Button Style Utilities
 * Centralized button styling patterns for consistency across the app
 */

/**
 * TOOLBAR DESIGN SYSTEM STANDARDS
 *
 * Standard Toolbar Styling:
 * - Background: bg-background/85 (consistent opacity across all toolbars)
 * - Backdrop blur: backdrop-blur-lg (consistent blur strength)
 * - Border: border-t border-white/10 (top border for toolbars below header)
 *           border-b border-white/10 (bottom border for sticky toolbars)
 * - Height: h-14 (56px) for standard toolbars, h-[48px] for list headers
 * - Z-index: z-[25] for main toolbars, z-[22] for list sticky headers
 * - Positioning: fixed with top-14 md:top-[70px] (accounting for header height)
 *
 * Positioning Exceptions:
 * - Portfolio & Sealed Products: Can use sticky top-0 or sticky top-16 md:top-20
 *   (full-page views with back buttons, not overlaying filter panels)
 * - ListStickyHeaderBar: Uses h-[48px] and z-[22] as specialized component
 */

/**
 * PRIMARY TOOLBAR CONTROLS
 * Standard pattern for dropdowns, filters, and search inputs
 * Purple default → 50% gold hover → 100% gold active
 */

// Standard interactive border states (white hover pattern)
export const interactiveBorderHover = 'can-hover:hover:border-white/20';
export const interactiveBorderFocus = 'focus:border-white/30 focus-visible:border-white/30';
export const interactiveBorderActive = 'active:border-white/40';

// Combined pattern for all interactive elements
export const interactiveBorder = cn(
	interactiveBorderHover,
	interactiveBorderFocus,
	interactiveBorderActive,
	'transition-colors'
);

// Standard interactive background states (white opacity scale)
export const interactiveBgHover = 'can-hover:hover:bg-white/[0.05]';
export const interactiveBgFocus = 'focus:bg-white/[0.08]';
export const interactiveBgActive = 'active:bg-white/10';
export const interactiveBgSelected = 'data-[state=on]:bg-white/10 data-[state=checked]:bg-white/10';
export const interactiveBgOpen = 'data-[state=open]:bg-white/[0.08]';

// Combined background pattern for list items, table rows, etc.
export const interactiveBg = cn(
	interactiveBgHover,
	interactiveBgActive,
	'transition-colors'
);

// Base toolbar control pattern (most common interactive pattern)
export const toolbarControl = cn(
	'border border-input transition-all backdrop-blur-0 shadow-none',
	interactiveBorderHover,
	'data-[state=open]:border-white/40', // For Select/Popover triggers
	'focus-visible:border-white/30' // For Input fields
);

// Specific variants for different component types
export const toolbarSelectTrigger = cn(toolbarControl, 'h-10 w-auto px-4');

export const toolbarPopoverTrigger = cn(toolbarControl, 'h-10 px-4 justify-start');

export const toolbarInput = cn(
	toolbarControl,
	'h-10 bg-[hsl(var(--toolbar-control-bg,var(--background)))] hover:bg-[hsl(var(--toolbar-control-bg,var(--background)))] !shadow-none'
);

// Icon button for toolbars (Sort, Column Selector)
export const toolbarIconButton = cn(
	'h-10 w-10 bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
	toolbarControl,
	'transition-colors'
);

// Popover/Dropdown button for toolbars (Ink Colors, Price Range, Format)
export const toolbarPopoverButton = cn(
	'h-10 px-4 bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
	toolbarControl,
	'transition-colors'
);

/**
 * Border Pattern Utilities
 * Standardized border styling for consistent interactive elements
 */

// Default card/panel border (2px solid)
export const borderDefault = 'border-2 border-primary/30';

// Hover state border (strengthened feedback)
export const borderHover = 'can-hover:hover:border-white/20';

// Active/selected state border (full strength)
export const borderActive = 'border-primary/90';

// Subtle border (1px, for dividers)
export const borderSubtle = 'border border-primary/20';

// Button border (1px, slightly stronger than subtle)
export const borderButton = 'border border-primary/40';

// Combined patterns for common use cases
export const borderDefaultHover = cn(borderDefault, borderHover);
export const borderButtonHover = cn(borderButton, borderHover);

// Standard outline icon button (ViewToggle, filters, etc.)
export const iconButtonOutline = cn(
	'transition-all focus-visible:ring-0 focus-visible:ring-offset-0',
	'[&_svg]:!size-[26px]',
	borderButtonHover,
	'h-10 w-10 bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
	'can-hover:hover:bg-[hsl(var(--toolbar-control-bg,var(--background)))]'
);

// Text button with dropdown (MobileViewModeSelector, SortButton, etc.)
export const textButtonOutline = cn(
	borderButton,
	'can-hover:hover:border-white/20 focus:border-white/30 active:border-white/40',
	'bg-[hsl(var(--toolbar-control-bg,var(--background)))] can-hover:hover:bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
	'transition-all focus-visible:ring-0 focus-visible:ring-offset-0'
);

// Toggle badge base styles (shared between all toggle states)
export const toggleBadgeBase = cn(
	'cursor-pointer transition-all duration-150 px-3 py-1 text-xs',
	'focus:ring-0 focus:ring-offset-0 active:scale-95',
	'can-hover:hover:bg-transparent focus:bg-transparent',
	'active:bg-transparent focus-visible:bg-transparent'
);

// Normal finish badge (active state)
export const normalFinishActive = cn(toggleBadgeBase, primaryGradientActive);

// Foil finish badge (active state)
export const foilFinishActive = cn(
	toggleBadgeBase,
	'bg-gradient-to-br from-secondary via-secondary to-purple-700',
	'border-secondary text-white can-hover:hover:opacity-70'
);

// Toggle badge inactive state
export const toggleBadgeInactive = cn(
	toggleBadgeBase,
	'text-muted-foreground/40 border border-gray-600/30',
	'can-hover:hover:border-gray-600/40'
);

/**
 * GLOW PATTERNS
 * Standardized subtle glow effects for selected states
 */

// Standard selected state glow (subtle white) - used on mode toggles, visibility toggles
export const selectedGlow = 'shadow-[0_0_10px_rgba(255,255,255,0.06)]';

// Standard selected state glow (cyan primary) - used on primary action toggles
export const selectedGlowPrimary = 'shadow-[0_0_10px_rgba(0,165,223,0.15)]';

// Standard active ring glow (cyan primary) - used on icon buttons
export const activeRingGlow = 'ring-1 ring-primary/50';

/**
 * GLASS FILL PATTERNS
 * Standard background fills for consistent glass aesthetic
 */

// Standard glass fill for buttons, containers, list items
export const glassFill = 'bg-black/20';

// Slightly darker glass for nested/summary sections
export const glassFillDark = 'bg-black/30';

// Combined glass pattern (fill + border + blur)
export const glassContainer = cn(
	glassFill,
	'backdrop-blur-md border border-white/[0.08]'
);
